import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  DollarSign, User, AlertCircle, Clock, CheckCircle, 
  X, Loader2, Search, Building2, CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusBadges = {
  pending: 'badge-error',
  partial: 'badge-warning',
  completed: 'badge-success'
};

const Credits = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalOwed: 0, totalPaid: 0, pendingCount: 0, partialCount: 0, clientCount: 0, supplierCount: 0 });
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get('/credits');
      const data = response.data;
      setCredits(data.credits || []);
      setSummary(data.summary || { totalOwed: 0, totalPaid: 0, pendingCount: 0, partialCount: 0, clientCount: 0, supplierCount: 0 });
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load credit/debt data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const credit = selectedCredit;
      if (credit.credit_type === 'supplier') {
        await api.post(`/credits/supplier-payment/${credit.id}`, {
          amount: parseFloat(paymentAmount)
        });
      } else {
        await api.post(`/credits/client-payment/${credit.id}`, {
          amount: parseFloat(paymentAmount)
        });
      }
      toast.success('Payment recorded successfully');
      setSelectedCredit(null);
      setPaymentAmount('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
    setSubmitting(false);
  };

  const filteredCredits = credits.filter(credit => {
    const matchesSearch = credit.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      credit.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'clients') return matchesSearch && credit.credit_type === 'client';
    if (activeTab === 'suppliers') return matchesSearch && credit.credit_type === 'supplier';
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <DollarSign size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Credit & Debts</h1>
            <p className="text-sm text-neutral-gray">Manage outstanding payments across all entities</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <p className="text-sm text-neutral-gray">Total Owed</p>
          <p className="text-2xl font-bold text-brand-red mt-1">KES {summary.totalOwed.toLocaleString()}</p>
          <div className="p-2 bg-brand-red-100 rounded-lg inline-block mt-2">
            <DollarSign size={16} className="text-brand-red" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <p className="text-sm text-neutral-gray">Total Paid</p>
          <p className="text-2xl font-bold text-brand-green mt-1">KES {summary.totalPaid.toLocaleString()}</p>
          <div className="p-2 bg-brand-green-100 rounded-lg inline-block mt-2">
            <DollarSign size={16} className="text-brand-green" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <p className="text-sm text-neutral-gray">Client Credits</p>
          <p className="text-2xl font-bold text-brand-blue mt-1">{summary.clientCount}</p>
          <div className="p-2 bg-brand-blue-100 rounded-lg inline-block mt-2">
            <User size={16} className="text-brand-blue" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <p className="text-sm text-neutral-gray">Supplier Debts</p>
          <p className="text-2xl font-bold text-brand-orange-700 mt-1">{summary.supplierCount}</p>
          <div className="p-2 bg-brand-orange-100 rounded-lg inline-block mt-2">
            <Building2 size={16} className="text-brand-orange" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-neutral-light rounded-xl p-1">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'all' ? 'bg-white text-brand-green shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
          }`}
        >
          All ({credits.length})
        </button>
        <button
          onClick={() => setActiveTab('clients')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'clients' ? 'bg-white text-brand-green shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
          }`}
        >
          Clients ({summary.clientCount})
        </button>
        <button
          onClick={() => setActiveTab('suppliers')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'suppliers' ? 'bg-white text-brand-green shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
          }`}
        >
          Suppliers ({summary.supplierCount})
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-gray" />
        <input
          type="text"
          placeholder="Search by name or receipt..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
        />
      </div>

      {/* Content */}
      {filteredCredits.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle size={48} className="mx-auto text-brand-green-300 mb-3" />
          <p className="text-neutral-gray font-medium">No outstanding balances!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCredits.map(credit => (
            <div key={`${credit.credit_type}-${credit.id}`} className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    credit.credit_type === 'supplier' ? 'bg-brand-orange-100' : 'bg-brand-red-100'
                  }`}>
                    {credit.credit_type === 'supplier' ? (
                      <Building2 size={20} className="text-brand-orange" />
                    ) : (
                      <User size={20} className="text-brand-red" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-neutral-dark">{credit.client_name || 'Unknown'}</p>
                    <p className="text-xs text-neutral-gray">{credit.receipt_number}</p>
                    <p className="text-xs text-neutral-gray">{credit.client_phone}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-brand-red">KES {parseFloat(credit.balance_due).toLocaleString()}</p>
                  <div className="flex items-center space-x-2 justify-end mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadges[credit.payment_status] || 'badge-warning'}`}>
                      {credit.payment_status}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      credit.credit_type === 'supplier' ? 'bg-brand-orange-100 text-brand-orange' : 'bg-brand-blue-100 text-brand-blue'
                    }`}>
                      {credit.credit_type === 'supplier' ? 'Debt' : 'Credit'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-neutral-gray">
                  Total: KES {parseFloat(credit.total_amount).toLocaleString()} · 
                  Paid: KES {parseFloat(credit.amount_paid).toLocaleString()}
                </p>
                <button
                  onClick={() => { setSelectedCredit(credit); setPaymentAmount(credit.balance_due); }}
                  className="text-xs text-brand-green hover:text-brand-green-700 font-medium"
                >
                  Record Payment
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Payment Modal */}
      {selectedCredit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">Record Payment</h2>
              <button onClick={() => { setSelectedCredit(null); setPaymentAmount(''); }} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedCredit.credit_type === 'supplier' ? 'bg-brand-orange-100' : 'bg-brand-red-100'
                }`}>
                  {selectedCredit.credit_type === 'supplier' ? (
                    <Building2 size={20} className="text-brand-orange" />
                  ) : (
                    <User size={20} className="text-brand-red" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-neutral-dark">{selectedCredit.client_name}</p>
                  <p className="text-sm text-neutral-gray">{selectedCredit.receipt_number}</p>
                </div>
              </div>

              <div className="bg-neutral-light rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-gray">Total Amount</span>
                  <span className="text-neutral-dark font-medium">KES {parseFloat(selectedCredit.total_amount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-gray">Already Paid</span>
                  <span className="text-brand-green">KES {parseFloat(selectedCredit.amount_paid).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-neutral-medium pt-2 mt-2">
                  <span className="text-brand-red font-semibold">Balance Due</span>
                  <span className="text-brand-red font-bold text-lg">KES {parseFloat(selectedCredit.balance_due).toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-medium rounded-xl text-lg font-semibold focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  min="0"
                  max={selectedCredit.balance_due}
                  step="0.01"
                />
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setPaymentAmount(selectedCredit.balance_due)}
                  className="flex-1 px-3 py-1.5 bg-brand-green-50 text-brand-green rounded-lg text-sm font-medium hover:bg-brand-green-100"
                >
                  Full Amount
                </button>
                <button
                  onClick={() => setPaymentAmount((parseFloat(selectedCredit.balance_due) / 2).toFixed(2))}
                  className="flex-1 px-3 py-1.5 bg-brand-blue-50 text-brand-blue rounded-lg text-sm font-medium hover:bg-brand-blue-100"
                >
                  Half
                </button>
              </div>

              <button
                onClick={handlePayment}
                disabled={submitting}
                className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors bg-brand-green text-white hover:bg-brand-green-700 ${submitting ? 'opacity-50' : ''}`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center space-x-2">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Recording...</span>
                  </span>
                ) : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Credits;